const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const DESKTOP_VIEW_STORAGE_KEY = 'tradeJournal:listView';

const CHART_UP = '#07CBA3';
const CHART_DOWN = '#F73263';

/** App chart up/down colors, with a light tint for pill backgrounds. */
export function journalPnlTone(colors, numeric) {
  if (numeric == null || !Number.isFinite(numeric) || numeric === 0) {
    return { color: colors?.textMuted || '#7B8A9E', backgroundColor: 'rgba(148, 163, 184, 0.12)' };
  }
  const hex = numeric > 0 ? (colors?.chartUp || CHART_UP) : (colors?.chartDown || CHART_DOWN);
  return { color: hex, backgroundColor: `${hex}26` };
}

/** Same subtle purple gradient border for every journal entry and field pill. */
export function journalPurpleBorderStyle(colors, background, { strong = false } = {}) {
  const bg = background || colors?.bgSecondary || '#101420';
  const primary = colors?.primary || '#6366F1';
  const primaryLight = colors?.primaryLight || '#8B5CF6';
  const edge = strong ? '99' : '55';
  const fade = strong ? '44' : '22';
  return {
    border: '1px solid transparent',
    backgroundColor: bg,
    backgroundImage: `linear-gradient(${bg}, ${bg}), linear-gradient(135deg, ${primary}${edge}, ${primaryLight}${fade})`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  };
}

export const formatCurrency = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return currencyFormatter.format(num);
};

const getKey = (f) => f?.key || f?.label;

export const DEFAULT_JOURNAL_TICKER_KEY = 'ticker';
export const DEFAULT_JOURNAL_DATETIME_KEY = 'tradeTime';

const isTickerField = (f) => {
  const key = (getKey(f) || '').toLowerCase();
  return key.includes('ticker') || key.includes('symbol');
};

const isPnlField = (f) => {
  const key = (getKey(f) || '').toLowerCase();
  return (
    key.includes('pnl') ||
    key.includes('profit') ||
    key.includes('loss') ||
    key.includes('p&l')
  );
};

const isNotesField = (f) => {
  const key = (getKey(f) || '').toLowerCase();
  return (
    f.type === 'textarea' ||
    key.includes('note') ||
    key.includes('comment') ||
    key.includes('description') ||
    key.includes('summary')
  );
};

export const getSortedTemplateFields = (entry) => {
  const fields = Array.isArray(entry?.template?.fields) ? entry.template.fields : [];
  return fields
    .map((f, i) => ({ ...f, __i: i }))
    .sort((a, b) => ((a.position ?? a.__i) - (b.position ?? b.__i)));
};

export const getTickerFromJournal = (journal) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const fields = getSortedTemplateFields(e);
  for (const field of fields) {
    const key = getKey(field);
    const keyLower = (key || '').toLowerCase();
    if ((keyLower.includes('ticker') || keyLower.includes('symbol')) && field.type === 'text') {
      const ticker = values[key] ?? e[key];
      if (ticker && String(ticker).trim()) return String(ticker).trim().toUpperCase();
    }
  }
  for (const [key, value] of Object.entries(values)) {
    const keyLower = key.toLowerCase();
    if ((keyLower.includes('ticker') || keyLower.includes('symbol')) && value && String(value).trim()) {
      return String(value).trim().toUpperCase();
    }
  }
  if (values[DEFAULT_JOURNAL_TICKER_KEY] && String(values[DEFAULT_JOURNAL_TICKER_KEY]).trim()) {
    return String(values[DEFAULT_JOURNAL_TICKER_KEY]).trim().toUpperCase();
  }
  return '—';
};

export const getJournalDateRaw = (journal) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const fields = getSortedTemplateFields(e);
  const datetimeField = fields.find((f) => f.type === 'datetime');
  const dateKey = datetimeField ? getKey(datetimeField) : null;
  return (dateKey ? (values[dateKey] ?? e[dateKey]) : null)
    || values[DEFAULT_JOURNAL_DATETIME_KEY]
    || e.createdAt
    || null;
};

export const formatFieldValue = (field, raw, { formatJournalDate } = {}) => {
  if (raw == null || raw === '') return '';
  let v = raw;
  if (field.type === 'datetime' && formatJournalDate) v = formatJournalDate(raw);
  if (field.type === 'checkbox') v = Boolean(raw) ? 'Yes' : 'No';
  if (field.type === 'currency') v = formatCurrency(raw);
  if (Array.isArray(v)) v = v.join(', ');
  return String(v);
};

export const getJournalChips = (journal, { formatJournalDate } = {}) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const journalInfoFields = getSortedTemplateFields(e).filter(
    (f) => f.type !== 'datetime' && !isTickerField(f)
  );
  return journalInfoFields.map((f) => {
    const key = getKey(f);
    const raw = values[key] ?? e[key];
    const value = formatFieldValue(f, raw, { formatJournalDate });
    return { label: f.label || key, value: value !== '' ? value : '—', fieldKey: key, field: f };
  });
};

export const getJournalPnl = (journal) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const fields = getSortedTemplateFields(e).filter((f) => !isTickerField(f) && f.type !== 'datetime');

  const pnlField =
    fields.find((f) => isPnlField(f) && (f.type === 'currency' || f.type === 'number')) ||
    fields.find((f) => isPnlField(f));

  if (pnlField) {
    const key = getKey(pnlField);
    const raw = values[key] ?? e[key];
    if (raw != null && raw !== '') {
      const num = Number(raw);
      const formatted = pnlField.type === 'currency' ? formatCurrency(raw) : String(raw);
      return {
        label: pnlField.label || key,
        formatted,
        numeric: Number.isFinite(num) ? num : null,
      };
    }
  }

  // Linked journals receive the authoritative realized P&L from their trade row.
  if (journal.trade_pnl != null && journal.trade_pnl !== '') {
    const num = Number(journal.trade_pnl);
    return {
      label: 'P&L',
      formatted: formatCurrency(journal.trade_pnl),
      numeric: Number.isFinite(num) ? num : null,
    };
  }

  return null;
};

export const getNotesPreview = (journal, maxLen = 120) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const fields = getSortedTemplateFields(e);
  const notesField = fields.find((f) => isNotesField(f));
  if (!notesField) return '';

  const key = getKey(notesField);
  const raw = values[key] ?? e[key];
  if (raw == null || raw === '') return '';

  const text = String(raw).replace(/\s+/g, ' ').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
};

export const getJournalCompletion = (journal) => {
  const e = journal.entry || {};
  const values = e.values || {};
  const trackable = getSortedTemplateFields(e).filter(
    (f) => f.type !== 'datetime' && !isTickerField(f)
  );

  if (trackable.length === 0) {
    return { percent: 100, label: 'Complete', tone: 'complete' };
  }

  const filled = trackable.filter((f) => {
    const raw = values[getKey(f)] ?? e[getKey(f)];
    if (raw == null || raw === '') return false;
    if (Array.isArray(raw) && raw.length === 0) return false;
    return true;
  }).length;

  const percent = Math.round((filled / trackable.length) * 100);
  if (percent >= 100) return { percent, label: 'Complete', tone: 'complete' };
  if (percent >= 50) return { percent, label: 'In progress', tone: 'progress' };
  if (percent > 0) return { percent, label: 'Started', tone: 'started' };
  return { percent, label: 'Draft', tone: 'draft' };
};

export const getDesktopDateParts = (value) => {
  try {
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return { time: '', date: '' };
    const hours = dt.getHours();
    const minutes = dt.getMinutes();
    const h12 = hours % 12 === 0 ? 12 : hours % 12;
    const mm = String(minutes).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const time = `${h12}:${mm} ${ampm}`;
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const dow = days[dt.getDay()];
    const day = dt.getDate();
    const suffix = (d) => {
      const v = d % 100;
      if (v >= 11 && v <= 13) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const month = months[dt.getMonth()];
    const year = dt.getFullYear();
    return { time, date: `${dow}, ${month} ${day}${suffix(day)}, ${year}` };
  } catch (_) {
    return { time: '', date: '' };
  }
};

/** Same symbol resolution used by TradeJournalModal header. */
export function resolveJournalTradeSymbol(positionData, connectedTrade) {
  const raw =
    positionData?.Symbol ||
    positionData?.symbol ||
    connectedTrade?.Symbol ||
    connectedTrade?.symbol ||
    null;
  if (raw == null || raw === '') return null;
  const trimmed = String(raw).trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

export function findJournalTickerFieldKey(fields = []) {
  for (const field of fields) {
    const key = getKey(field);
    if (!key) continue;
    const keyLower = key.toLowerCase();
    if (field.type === 'text' && (keyLower.includes('ticker') || keyLower.includes('symbol'))) {
      return key;
    }
  }
  return null;
}

/** Datetime field key from template, if present. */
export function findJournalDatetimeFieldKey(fields = []) {
  for (const field of fields) {
    const key = getKey(field);
    if (!key) continue;
    if (field.type === 'datetime') return key;
    const keyLower = key.toLowerCase();
    if (keyLower.includes('datetime') || (keyLower.includes('date') && keyLower.includes('time'))) {
      return key;
    }
  }
  return null;
}

export function formatJournalDatetimeLocal(input) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/** Position open time for journal metadata — same sources as trade_id / performance trades. */
export function resolveJournalTradeOpenAt(positionData, connectedTrade) {
  const candidates = [
    positionData?.Timestamp,
    positionData?.OpenDate,
    positionData?.DateOpened,
    positionData?.OpenedDateTime,
    connectedTrade?.entryTime,
    connectedTrade?.openedAt,
    connectedTrade?.tradeDate,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return new Date();
}

export function resolveJournalTickerFromValues(values = {}, fields = []) {
  const fieldKey = findJournalTickerFieldKey(fields);
  if (fieldKey && values[fieldKey] && String(values[fieldKey]).trim()) {
    return String(values[fieldKey]).trim().toUpperCase();
  }
  for (const [key, value] of Object.entries(values)) {
    const keyLower = key.toLowerCase();
    if ((keyLower.includes('ticker') || keyLower.includes('symbol')) && value && String(value).trim()) {
      return String(value).trim().toUpperCase();
    }
  }
  return null;
}

export function resolveJournalDatetimeFromValues(values = {}, fields = []) {
  const fieldKey = findJournalDatetimeFieldKey(fields);
  if (fieldKey && values[fieldKey]) return values[fieldKey];
  for (const [key, value] of Object.entries(values)) {
    const keyLower = key.toLowerCase();
    if ((keyLower.includes('datetime') || (keyLower.includes('date') && keyLower.includes('time'))) && value) {
      return value;
    }
  }
  return null;
}

/** Inject required ticker + trade datetime into values (template field key or canonical default). */
export function buildJournalSavePayload({
  values = {},
  fields = [],
  tradeSymbol = null,
  tradeOpenAt = null,
} = {}) {
  const openAt = tradeOpenAt instanceof Date
    ? tradeOpenAt
    : resolveJournalTradeOpenAt(null, null);
  const datetimeLocal = formatJournalDatetimeLocal(openAt);
  const tickerFieldKey = findJournalTickerFieldKey(fields) || DEFAULT_JOURNAL_TICKER_KEY;
  const datetimeFieldKey = findJournalDatetimeFieldKey(fields) || DEFAULT_JOURNAL_DATETIME_KEY;

  const ticker =
    resolveJournalTickerFromValues(values, fields) ||
    (tradeSymbol ? String(tradeSymbol).trim().toUpperCase() : null);
  const datetime = resolveJournalDatetimeFromValues(values, fields) || datetimeLocal;

  const valuesToSave = { ...values };
  if (ticker) valuesToSave[tickerFieldKey] = ticker;
  if (datetime) valuesToSave[datetimeFieldKey] = datetime;

  let positionTimestamp;
  try {
    positionTimestamp = new Date(datetime || openAt).toISOString();
  } catch (_) {
    positionTimestamp = openAt.toISOString();
  }

  return { valuesToSave, ticker, datetime, positionTimestamp };
}
